import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { apiErr, formatNum } from "../lib/utils";
import { Button } from "./ui/button";
import { Input, Label } from "./ui/input";
import { Badge } from "./ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Plus, Trash2, Pencil, Check, X, Layers, FolderTree, ChevronDown, ChevronRight, Building2 } from "lucide-react";
import { toast } from "sonner";

const slug = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");

function Rollup({ g }) {
  return (
    <div className="flex items-center gap-3 text-[11px] font-mono text-muted-foreground shrink-0">
      <span data-testid="group-rollup-facilities" title="Facilities (incl. sub-groups)"><Building2 className="h-3 w-3 inline mr-0.5" />{g.facility_count}</span>
      <span data-testid="group-rollup-records" title="Records assigned">{formatNum(g.record_count)} rec</span>
      <span className="text-[#00E65B]" data-testid="group-rollup-emissions">{formatNum(g.emissions_t, { decimals: 1 })} tCO₂e</span>
      <span className="text-[#60A5FA]" data-testid="group-rollup-energy">{formatNum(g.energy_mwh, { decimals: 1 })} MWh</span>
    </div>
  );
}

export function GroupMaster({ canEdit }) {
  const [data, setData] = useState(null);
  const [newName, setNewName] = useState("");
  const [newParent, setNewParent] = useState("none");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [collapsed, setCollapsed] = useState({});

  const load = () => api.get("/facility-groups").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const topOptions = useMemo(() => (data?.groups || []).map((g) => ({ value: g.id, label: g.name })), [data]);

  const create = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await api.post("/facility-groups", { name, parent_id: newParent === "none" ? null : newParent });
      toast.success(newParent === "none" ? "Group created" : "Sub-group created");
      setNewName(""); setNewParent("none"); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const rename = async (g) => {
    const name = editName.trim();
    if (!name) return;
    try { await api.patch(`/facility-groups/${g.id}`, { name }); toast.success("Group renamed"); setEditing(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (g) => {
    if (!window.confirm(`Delete group '${g.name}'? Facilities and their records are never deleted — this only removes the group label.`)) return;
    try { await api.delete(`/facility-groups/${g.id}`); toast.success("Group deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!data) return <div className="p-6 text-muted-foreground font-mono text-sm">Loading groups…</div>;

  const GroupHeader = ({ g, sub }) => {
    const isEd = editing === g.id;
    const open = !collapsed[g.id];
    return (
      <div className={`flex items-center gap-2 py-2 ${sub ? "pl-6" : ""}`} data-testid={`group-row-${slug(g.name)}`}>
        {g.children?.length > 0 || (!sub && true)
          ? <button type="button" onClick={() => setCollapsed((c) => ({ ...c, [g.id]: !c[g.id] }))} className="text-muted-foreground hover:text-foreground" data-testid={`group-toggle-${slug(g.name)}`}>
              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          : <span className="w-4" />}
        {sub ? <FolderTree className="h-3.5 w-3.5 text-accent/70 shrink-0" /> : <Layers className="h-4 w-4 text-accent shrink-0" />}
        {isEd ? (
          <div className="flex items-center gap-1">
            <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-7 w-48 text-xs" data-testid="group-edit-name" />
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => rename(g)} data-testid="group-save-btn"><Check className="h-3.5 w-3.5 text-[#00E65B]" /></Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing(null)}><X className="h-3.5 w-3.5 text-muted-foreground" /></Button>
          </div>
        ) : (
          <span className={`font-display font-semibold ${sub ? "text-sm" : ""}`}>{g.name}</span>
        )}
        <div className="flex-1" />
        <Rollup g={g} />
        {canEdit && !isEd && (
          <div className="flex items-center gap-0.5">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditing(g.id); setEditName(g.name); }} data-testid={`group-rename-btn-${slug(g.name)}`}><Pencil className="h-3.5 w-3.5 text-muted-foreground" /></Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove(g)} data-testid={`group-delete-btn-${slug(g.name)}`}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="card-flat" data-testid="group-master">
      <div className="p-4 border-b border-border bg-surface">
        <h2 className="font-display font-semibold text-lg flex items-center gap-2"><Layers className="h-4 w-4 text-accent" /> Group Master</h2>
        <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
          Classify your sites into groups (e.g. Offices, Nursery, Dormitories, Fleet), with one optional level of sub-groups.
          A group's numbers are always the sum of the sites inside it — assign facilities to groups in Settings → Facilities.
          Deleting a group never removes facilities or records, and is blocked until the group is empty.
        </p>
      </div>

      {canEdit && (
        <div className="p-4 border-b border-border flex flex-wrap items-end gap-3" data-testid="add-group-form">
          <div><Label>Group name</Label><Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Dormitories" className="w-56" data-testid="group-name-input" onKeyDown={(e) => e.key === "Enter" && create()} /></div>
          <div><Label>Sub-group of (optional)</Label>
            <Select value={newParent} onValueChange={setNewParent}>
              <SelectTrigger className="w-52" data-testid="group-parent-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Top-level group —</SelectItem>
                {topOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button variant="primary" size="sm" disabled={!newName.trim()} onClick={create} data-testid="add-group-btn"><Plus className="h-3.5 w-3.5" /> Add group</Button>
        </div>
      )}

      <div className="divide-y divide-border px-4">
        {data.groups.length === 0 && (
          <div className="py-10 text-center text-muted-foreground text-sm" data-testid="group-empty">No groups yet — create your first group above (e.g. Offices, Nursery, Dormitories, Fleet).</div>
        )}
        {data.groups.map((g) => (
          <div key={g.id}>
            <GroupHeader g={g} />
            {!collapsed[g.id] && (g.children || []).map((c) => <GroupHeader key={c.id} g={c} sub />)}
          </div>
        ))}
      </div>

      <div className="p-4 border-t border-border flex items-center gap-2 bg-surface/50" data-testid="group-ungrouped-summary">
        <Building2 className="h-4 w-4 text-[#FFB800]" />
        <span className="text-sm">Ungrouped</span>
        <Badge variant="outline" className="text-[10px]">default</Badge>
        <div className="flex-1" />
        <Rollup g={data.ungrouped} />
      </div>
    </div>
  );
}
